import { gsap } from 'gsap';
import { Container, Sprite } from '../../odie';

import { app } from '../app';
import { IAnimation } from '../../lib/pattern/IAnimation';
import { ParticleEmitter } from '../particles/ParticleEmitter';
import { getMatch2AssetPath } from '../util/assets';

// types
//-----------------------------------------------------------------------------
export type FallingLeafAnimationOptions = {
  leafIndex: number;
};

/*
    block breaking animation
*/
export class FallingLeafAnimation extends Container implements IAnimation {
  // fields
  //-------------------------------------------------------------------------
  private _options: FallingLeafAnimationOptions;

  // init
  //-------------------------------------------------------------------------
  constructor(options: FallingLeafAnimationOptions) {
    super();
    this._options = options;
  }

  public static assets(): string[] {
    return [
      'fx.block',
      'barbedwire/block.barbedwire.leaf0.png',
      'barbedwire/block.barbedwire.leaf1.png',
      'barbedwire/block.barbedwire.leaf2.png',
    ];
  }

  // impl
  //-------------------------------------------------------------------------
  public async start(): Promise<void> {
    const index = this._options.leafIndex;

    // create leaf emitter
    const leafEmitter = this._createLeafEmitter(index);

    // create blast
    const blast = this._createBlast();

    // animate leaf and blast
    await Promise.all([
      this._animateLeaf(leafEmitter),
      this._animateBlast(blast),
    ]);
  }

  public stop() {}

  // private: animators
  //-------------------------------------------------------------------------
  private async _animateLeaf(leaf: ParticleEmitter) {
    // spawn leaf
    this.addChild(leaf.view);

    // start
    await leaf.start();

    // despawn blast
    this.removeChild(leaf.view);
  }

  private async _animateBlast(blast: Sprite) {
    // set initial values
    blast.scaleX = 0.2;
    blast.scaleY = 0.2;

    // spawn blast
    this.addChild(blast);

    // animate
    await gsap.to(blast, {
      scaleX: 1.6,
      scaleY: 1.6,
      duration: 0.2,
      ease: 'power1.out',
    });

    // despawn blast
    this.removeChild(blast);
  }

  // private: factory
  //-------------------------------------------------------------------------
  private _createLeafEmitter(index: number): ParticleEmitter {
    return app.particles.create({
      textures: [
        getMatch2AssetPath(`barbedwire/block.barbedwire.leaf${index}.png`),
      ],
      rate: 0,
      limit: 1,
      emitterDuration: 0,
      duration: 2.5,
      velocity: { x: 0, y: -200 },
      scale: { x: 0.65, y: 0.65 },
      behaviors: [
        { type: 'explode', magnitude: [60, 180] },
        { type: 'gravity', gravity: { x: 0, y: 700 } },
        { type: 'scale', to: 0 },
        { type: 'kinematic' },
      ],
    });
  }

  private _createBlast(): Sprite {
    const blast = Sprite.from('fx.block/fx.block.explode.png');
    blast.anchorX = 0.5;
    blast.anchorY = 0.5;
    return blast;
  }
}
